import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// ==================== 5 OFFICIAL DESIGN TEMPLATES ====================

const DESIGN_TEMPLATES = {
  // Template 1: Cash App Clean — Ultra-minimal light (from designer HTML)
  cashapp_clean: {
    id: 'cashapp_clean',
    name: 'Cash App Clean',
    description: 'Ultra-minimal, clean design with warm gray backgrounds',
    mode: 'light',
    colors: {
      background: '#FAFAF8',
      surface: '#F5F3EE',
      card: '#FFFFFF',
      header: '#0D7377',
      header_gradient: 'linear-gradient(135deg, #0D7377, #14A89A)',
      primary: '#0D7377',
      primary_light: '#14A89A',
      primary_dark: '#0A5C5F',
      secondary: '#E85D26',
      secondary_light: '#FF7A45',
      accent: '#E85D26',
      accent_secondary: '#0D7377',
      text: '#111111',
      text_secondary: '#555555',
      text_muted: '#999999',
      text_on_primary: '#FFFFFF',
      border: '#E8E6E1',
      divider: '#F5F3EE',
      shadow: 'rgba(0,0,0,0.08)',
      success: '#0D7377',
      warning: '#E85D26',
      error: '#E53935',
      info: '#0D7377'
    },
    typography: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      headingFamily: "'Inter', sans-serif",
      headingWeight: '700',
      bodyWeight: '400'
    },
    spacing: {
      borderRadius: '16px',
      borderRadiusLg: '24px',
      borderRadiusSm: '12px',
      cardPadding: '20px',
      headerHeight: '64px'
    },
    effects: {
      shadow: '0 2px 8px rgba(0,0,0,0.08)',
      shadowLg: '0 4px 16px rgba(0,0,0,0.1)',
      shadowSm: '0 1px 4px rgba(0,0,0,0.06)',
      blur: '0px',
      glow: 'none'
    }
  },

  // Template 2: Duo Energy — Gamified dark (from designer HTML)
  duo_energy: {
    id: 'duo_energy',
    name: 'Duo Energy',
    description: 'Gamified, addictive dark design with vibrant accents',
    mode: 'dark',
    colors: {
      background: '#0F1117',
      surface: '#181B24',
      card: '#1F222D',
      header: '#181B24',
      header_gradient: 'linear-gradient(145deg, #1A3830, #0D2520)',
      primary: '#00DDB3',
      primary_light: '#00FFD0',
      primary_dark: '#00B892',
      secondary: '#FF6A3D',
      secondary_light: '#FF8A60',
      accent: '#FFD23F',
      accent_secondary: '#FF4F81',
      text: '#FFFFFF',
      text_secondary: 'rgba(255,255,255,0.5)',
      text_muted: 'rgba(255,255,255,0.25)',
      text_on_primary: '#0F1117',
      border: 'rgba(255,255,255,0.06)',
      divider: 'rgba(255,255,255,0.04)',
      shadow: 'rgba(0,0,0,0.3)',
      success: '#00DDB3',
      warning: '#FFD23F',
      error: '#FF4F81',
      info: '#00DDB3'
    },
    typography: {
      fontFamily: "'Inter', sans-serif",
      headingFamily: "'Bricolage Grotesque', 'Inter', sans-serif",
      headingWeight: '800',
      bodyWeight: '500'
    },
    spacing: {
      borderRadius: '16px',
      borderRadiusLg: '20px',
      borderRadiusSm: '12px',
      cardPadding: '16px',
      headerHeight: '56px'
    },
    effects: {
      shadow: '0 4px 12px rgba(0,0,0,0.4)',
      shadowLg: '0 8px 24px rgba(0,0,0,0.5)',
      shadowSm: '0 2px 6px rgba(0,0,0,0.3)',
      blur: '12px',
      glow: '0 0 20px rgba(0,221,179,0.15)'
    }
  },

  // Template 3: Spotify Nights — Immersive dark (from designer HTML)
  spotify_nights: {
    id: 'spotify_nights',
    name: 'Spotify Nights',
    description: 'Immersive dark design with subtle gradients',
    mode: 'dark',
    colors: {
      background: '#121212',
      surface: '#1A1A1A',
      card: '#222222',
      header: '#1A1A1A',
      header_gradient: 'linear-gradient(155deg, #1A3D2E, #0B2E22, #0A1F1B)',
      primary: '#1ED760',
      primary_light: '#1ED760',
      primary_dark: '#19B550',
      secondary: '#FF6B35',
      secondary_light: '#FF8A55',
      accent: '#FF4F9A',
      accent_secondary: '#B388FF',
      text: '#FFFFFF',
      text_secondary: 'rgba(255,255,255,0.6)',
      text_muted: 'rgba(255,255,255,0.35)',
      text_on_primary: '#121212',
      border: 'rgba(255,255,255,0.06)',
      divider: 'rgba(255,255,255,0.04)',
      shadow: 'rgba(0,0,0,0.4)',
      success: '#1ED760',
      warning: '#FF6B35',
      error: '#FF4F9A',
      info: '#448AFF'
    },
    typography: {
      fontFamily: "'Inter', sans-serif",
      headingFamily: "'Satoshi', 'Inter', sans-serif",
      headingWeight: '900',
      bodyWeight: '400'
    },
    spacing: {
      borderRadius: '12px',
      borderRadiusLg: '16px',
      borderRadiusSm: '8px',
      cardPadding: '16px',
      headerHeight: '56px'
    },
    effects: {
      shadow: '0 8px 24px rgba(0,0,0,0.5)',
      shadowLg: '0 16px 48px rgba(0,0,0,0.6)',
      shadowSm: '0 4px 12px rgba(0,0,0,0.4)',
      blur: '8px',
      glow: '0 0 15px rgba(30,215,96,0.1)'
    }
  },

  // Template 4: TikTok Feed — Content-first social dark (from designer HTML)
  tiktok_feed: {
    id: 'tiktok_feed',
    name: 'TikTok Feed',
    description: 'Bold, content-first social design with neon accents',
    mode: 'dark',
    colors: {
      background: '#0A0A0F',
      surface: '#14141F',
      card: '#1C1C2A',
      header: '#14141F',
      header_gradient: 'linear-gradient(165deg, #0C2B3A, #0A1520, #0A0A0F)',
      primary: '#00F5D4',
      primary_light: '#72EFDD',
      primary_dark: '#00C4A9',
      secondary: '#FF6B6B',
      secondary_light: '#FF8A8A',
      accent: '#C77DFF',
      accent_secondary: '#FFD166',
      text: '#FFFFFF',
      text_secondary: 'rgba(255,255,255,0.55)',
      text_muted: 'rgba(255,255,255,0.25)',
      text_on_primary: '#0A0A0F',
      border: 'rgba(255,255,255,0.08)',
      divider: 'rgba(255,255,255,0.04)',
      shadow: 'rgba(0,0,0,0.4)',
      success: '#00F5D4',
      warning: '#FFD166',
      error: '#FF6B6B',
      info: '#72EFDD'
    },
    typography: {
      fontFamily: "'Inter', sans-serif",
      headingFamily: "'Manrope', 'Inter', sans-serif",
      headingWeight: '800',
      bodyWeight: '500'
    },
    spacing: {
      borderRadius: '16px',
      borderRadiusLg: '20px',
      borderRadiusSm: '10px',
      cardPadding: '16px',
      headerHeight: '56px'
    },
    effects: {
      shadow: '0 4px 15px rgba(0,0,0,0.5)',
      shadowLg: '0 8px 30px rgba(0,0,0,0.6)',
      shadowSm: '0 2px 8px rgba(0,0,0,0.35)',
      blur: '10px',
      glow: '0 0 20px rgba(0,245,212,0.1)'
    }
  },

  // Template 5: Smooth Vibes — Warm, friendly (original YAS Connect)
  smooth_vibes: {
    id: 'smooth_vibes',
    name: 'Smooth Vibes',
    description: 'Warm, friendly design with teal and coral accents',
    mode: 'light',
    colors: {
      background: '#FEF9F3',
      surface: '#FFFFFF',
      card: '#FFFFFF',
      header: '#1B2D4F',
      header_gradient: 'linear-gradient(135deg, #1A5E6D, #1B2D4F, #267A8C)',
      primary: '#1B2D4F',
      primary_light: '#267A8C',
      primary_dark: '#155560',
      secondary: '#E86C3D',
      secondary_light: '#F08050',
      accent: '#E86C3D',
      accent_secondary: '#1B2D4F',
      text: '#1A1A1A',
      text_secondary: '#666666',
      text_muted: '#8A9AA8',
      text_on_primary: '#FFFFFF',
      border: '#E5E5E5',
      divider: '#F0F0F0',
      shadow: 'rgba(0,0,0,0.1)',
      success: '#10B981',
      warning: '#F59E0B',
      error: '#EF4444',
      info: '#3B82F6'
    },
    typography: {
      fontFamily: "'Inter', sans-serif",
      headingFamily: "'Fredoka', sans-serif",
      headingWeight: '700',
      bodyWeight: '400'
    },
    spacing: {
      borderRadius: '16px',
      borderRadiusLg: '24px',
      borderRadiusSm: '12px',
      cardPadding: '20px',
      headerHeight: '72px'
    },
    effects: {
      shadow: '0 4px 12px rgba(0,0,0,0.1)',
      shadowLg: '0 8px 24px rgba(0,0,0,0.15)',
      shadowSm: '0 2px 6px rgba(0,0,0,0.08)',
      blur: '0px',
      glow: 'none'
    }
  }
};

// Default template
const DEFAULT_TEMPLATE = 'yas_connect';

// UNIFIED DESIGN - Warm balanced theme for all programs
const YAS_CONNECT_THEME = {
  id: 'yas_connect',
  name: 'YAS Connect',
  description: 'Warm balanced professional theme',
  mode: 'light',
  colors: {
    background: '#F5F3EF',
    surface: '#FFFFFF',
    card: '#FFFFFF',
    header: '#1B2D4F',
    header_gradient: 'linear-gradient(145deg, #1B2D4F, #2A4470)',
    primary: '#E86C3D',
    primary_light: '#FF8A5C',
    primary_dark: '#C4542D',
    secondary: '#2D9EA3',
    secondary_light: '#4ECDC4',
    accent: '#FFB547',
    accent_secondary: '#4CAF50',
    text: '#1A2A3A',
    text_secondary: '#5A6B7A',
    text_muted: '#8A96A3',
    text_on_primary: '#FFFFFF',
    border: '#E8E5E0',
    divider: '#F0EDE8',
    shadow: 'rgba(0,0,0,0.08)',
    success: '#4CAF50',
    warning: '#FFB547',
    error: '#E53935',
    info: '#2D9EA3'
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    headingFamily: "'Inter', sans-serif",
    headingWeight: '700',
    bodyWeight: '400'
  },
  spacing: {
    borderRadius: '16px',
    borderRadiusLg: '24px',
    borderRadiusSm: '10px',
    cardPadding: '20px',
    headerHeight: '64px'
  },
  effects: {
    shadow: '0 2px 12px rgba(0,0,0,0.3)',
    shadowLg: '0 8px 32px rgba(0,0,0,0.4)',
    shadowSm: '0 1px 4px rgba(0,0,0,0.2)',
    blur: '12px',
    glow: '0 0 20px rgba(124,92,252,0.15)'
  }
};

// Override ALL templates to use the unified design
Object.keys(DESIGN_TEMPLATES).forEach(key => {
  DESIGN_TEMPLATES[key] = { ...YAS_CONNECT_THEME, id: key, name: DESIGN_TEMPLATES[key].name };
});
DESIGN_TEMPLATES['yas_connect'] = YAS_CONNECT_THEME;

const DEFAULT_THEME = YAS_CONNECT_THEME;

// Create context
const ThemeContext = createContext(null);

// Apply CSS variables to document
const applyCSSVariables = (template) => {
  const root = document.documentElement;
  const { colors, typography, spacing, effects, mode } = template;
  
  // Set mode class
  root.classList.remove('theme-light', 'theme-dark');
  root.classList.add(`theme-${mode}`);
  
  // Colors
  Object.entries(colors).forEach(([key, value]) => {
    root.style.setProperty(`--color-${key.replace(/_/g, '-')}`, value);
  });
  
  // Typography
  root.style.setProperty('--font-family', typography.fontFamily);
  root.style.setProperty('--font-heading', typography.headingFamily);
  root.style.setProperty('--font-weight-heading', typography.headingWeight);
  root.style.setProperty('--font-weight-body', typography.bodyWeight);
  
  // Spacing
  root.style.setProperty('--radius', spacing.borderRadius);
  root.style.setProperty('--radius-lg', spacing.borderRadiusLg);
  root.style.setProperty('--radius-sm', spacing.borderRadiusSm);
  root.style.setProperty('--card-padding', spacing.cardPadding);
  root.style.setProperty('--header-height', spacing.headerHeight);
  
  // Effects
  root.style.setProperty('--shadow', effects.shadow);
  root.style.setProperty('--shadow-lg', effects.shadowLg);
  root.style.setProperty('--shadow-sm', effects.shadowSm);
  root.style.setProperty('--blur', effects.blur);
  root.style.setProperty('--glow', effects.glow);

  // Legacy variables for backward compatibility
  root.style.setProperty('--theme-primary', colors.primary);
  root.style.setProperty('--theme-primary-light', colors.primary_light);
  root.style.setProperty('--theme-primary-dark', colors.primary_dark);
  root.style.setProperty('--theme-secondary', colors.secondary);
  root.style.setProperty('--theme-secondary-light', colors.secondary_light);
  root.style.setProperty('--theme-accent', colors.accent);
  root.style.setProperty('--theme-background', colors.background);
  root.style.setProperty('--theme-surface', colors.surface);
  root.style.setProperty('--theme-text', colors.text);
  root.style.setProperty('--theme-text-muted', colors.text_muted);
};

export const ThemeProvider = ({ children, user }) => {
  const [currentTemplate, setCurrentTemplate] = useState(DEFAULT_TEMPLATE);
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customization, setCustomization] = useState({
    logo_url: null,
    logo_base64: null,
    program_name: 'Youth Program',
    header_text: 'Youth Achievement & Support'
  });

  // Fetch theme from API
  const fetchTheme = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const savedTemplate = localStorage.getItem('yas_theme_template');
      
      if (!token) {
        // No auth - use localStorage or default
        const fallbackId = savedTemplate || DEFAULT_TEMPLATE;
        const fallbackTheme = DESIGN_TEMPLATES[fallbackId] || DEFAULT_THEME;
        setCurrentTemplate(fallbackId);
        setTheme(fallbackTheme);
        applyCSSVariables(fallbackTheme);
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/theme/my-theme`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        // Map old template names to new ones
        let templateId = data.design_template || DEFAULT_TEMPLATE;
        
        // Force unified YAS Connect theme for ALL templates
        // Individual theme toggle is disabled — one consistent design
        const selectedTemplate = DEFAULT_THEME;
        
        setCurrentTemplate(templateId);
        setTheme(selectedTemplate);
        setCustomization({
          logo_url: data.logo_url,
          logo_base64: data.logo_base64,
          program_name: data.program_name || 'Youth Program',
          header_text: data.header_text || 'Youth Achievement & Support'
        });
        
        applyCSSVariables(selectedTemplate);
      } else {
        applyCSSVariables(DEFAULT_THEME);
      }
    } catch (err) {
      console.error('Failed to fetch theme:', err);
      setError(err.message);
      applyCSSVariables(DEFAULT_THEME);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    fetchTheme();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchTheme]);

  // Switch template
  const switchTemplate = useCallback((templateId) => {
    const newTemplate = DESIGN_TEMPLATES[templateId];
    if (newTemplate) {
      setCurrentTemplate(templateId);
      setTheme(newTemplate);
      applyCSSVariables(newTemplate);
      // Persist to localStorage for immediate cross-page consistency
    // eslint-disable-next-line react-hooks/exhaustive-deps
      localStorage.setItem('yas_theme_template', templateId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh theme
    // eslint-disable-next-line react-hooks/exhaustive-deps
  const refreshTheme = useCallback(async () => {
    setLoading(true);
    await fetchTheme();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchTheme]);
    // eslint-disable-next-line react-hooks/exhaustive-deps

  // Get logo
  const getLogo = useCallback(() => {
    return customization.logo_base64 || customization.logo_url || null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customization]);

  // Value object
  const value = {
    // Theme data
    theme,
    currentTemplate,
    templates: DESIGN_TEMPLATES,
    
    // Status
    loading,
    error,
    
    // Actions
    switchTemplate,
    refreshTheme,
    getLogo,
    
    // Convenience accessors
    colors: theme.colors,
    typography: theme.typography,
    spacing: theme.spacing,
    effects: theme.effects,
    mode: theme.mode,
    
    // Customization
    programName: customization.program_name,
    headerText: customization.header_text,
    logoUrl: customization.logo_url,
    
    // Legacy compatibility
    designTemplate: currentTemplate,
    templateConfig: {
      name: theme.name,
      description: theme.description
    }
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Hook to use theme
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    // Return defaults for use outside provider
    return {
      theme: DEFAULT_THEME,
      currentTemplate: DEFAULT_TEMPLATE,
      templates: DESIGN_TEMPLATES,
      loading: false,
      error: null,
      switchTemplate: () => {},
      refreshTheme: () => {},
      getLogo: () => null,
      colors: DEFAULT_THEME.colors,
      typography: DEFAULT_THEME.typography,
      spacing: DEFAULT_THEME.spacing,
      effects: DEFAULT_THEME.effects,
      mode: DEFAULT_THEME.mode,
      programName: 'Youth Program',
      headerText: 'Youth Achievement & Support',
      logoUrl: null,
      designTemplate: DEFAULT_TEMPLATE,
      templateConfig: {
        name: DEFAULT_THEME.name,
        description: DEFAULT_THEME.description
      }
    };
  }
  return context;
};

export { DESIGN_TEMPLATES, DEFAULT_THEME, DEFAULT_TEMPLATE };
